import { useState, Fragment } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import ListCheck2Icon from "remixicon-react/ListCheck2Icon";
import { List, ListItem, Collapse, Box } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import { useSnackbar } from "$utils/useSnackbar";
import { AxiosResponse } from "axios";
import { Layout, Text, InfoContainer, Popin, Button } from "$components";
import {
  addCategory,
  editCategory,
  deleteCategory,
  getCategory,
} from "../../api/categoryApi";
import useStyles from "./styles/Category.styles";
import useAuth from "$hooks/useAuth";
import { Category as CategoryType } from "$types/CategoryType";
import { formatCategory } from "$utils/categoryUtils";
import { isReferencingCategoryName } from "$hooks/useCategoriesQuery";
import {
  snackbarErrorOptions,
  snackbarSuccessOptions,
} from "$utils/SnackbarOptions";
import { CacheInvalidationAction } from "$utils/cache";
import CategoryForm from "./CategoryForm";

interface CategoryResponse {
  categories: any[];
}

interface ErrorResponse {
  error: string;
}

function Category() {
  const params = useParams<"idCompany">();
  const { idCompany } = params;
  const classes = useStyles();
  const { token } = useAuth();
  const [opened, setOpened] = useState<string[]>([]);
  const [openSuccessSnackbar] = useSnackbar(snackbarSuccessOptions);
  const [openErrorSnackbar] = useSnackbar(snackbarErrorOptions);
  const [activeCategory, setActiveCategory] = useState<any>();
  const [openPopin, toggleOpenPopin] = useState<boolean>(false);

  // Fetch categories
  const {
    data: categories = { groupedCategories: [], orderedCategories: [] },
  } = useQuery({
    queryKey: [CacheInvalidationAction.GET_CATEGORIES, idCompany],
    queryFn: () => getCategory(token, idCompany),

    select: (response: AxiosResponse<CategoryResponse> | ErrorResponse) => {
      // Handle possible error response
      if ("error" in response) {
        throw new Error(response.error as string);
      }

      // Extract categories array from response and format it
      return formatCategory(response.data.categories);
    },

    enabled: !!idCompany && !!token,
  });

  // Add category mutation
  const addMutation = useMutation({
    mutationFn: (data: any) =>
      addCategory(token, { ...data, company: idCompany }),

    onSuccess: () => {
      toggleOpenPopin(false);
      setActiveCategory(undefined);
      openSuccessSnackbar("Catégorie créée avec succès");
    },

    onError: (error: Error) => {
      openErrorSnackbar(error.message);
    },
  });

  // Edit category mutation
  const editMutation = useMutation({
    mutationFn: ({ data, id }: { data: any; id: string }) =>
      editCategory(token, { ...data, company: idCompany }, id),

    onSuccess: () => {
      toggleOpenPopin(false);
      setActiveCategory(undefined);
      openSuccessSnackbar("Catégorie modifiée avec succès");
    },

    onError: (error: Error) => {
      openErrorSnackbar(error.message);
    },
  });

  // Delete category mutation
  const deleteMutation = useMutation({
    mutationFn: (categoryId: string) => deleteCategory(token, categoryId),

    onSuccess: () => {
      openSuccessSnackbar("Catégorie supprimée avec succès");
    },

    onError: (error: Error) => {
      openErrorSnackbar(error.message);
    },
  });

  const toggleOpened = (ref: string) => {
    setOpened((prev) =>
      prev.includes(ref) ? prev.filter((o) => o !== ref) : [...prev, ref]
    );
  };

  const onSubmitHandler = async (data: any) => {
    if (data.id) {
      editMutation.mutate({ data, id: data.id });
    } else {
      addMutation.mutate(data);
    }
  };

  const handleDelete = (categoryId: string) => {
    deleteMutation.mutate(categoryId);
  };

  const isReferencementCategory = (category: CategoryType) => {
    return isReferencingCategoryName(category.title || "");
  };

  const renderCategory = (level: number, c: CategoryType, index: number) => {
    const isReferencement = isReferencementCategory(c);

    return (
      <Fragment key={`main_category_${index}`}>
        <ListItem className={classes.flexLine}>
          <div
            className={classes.flexLineLeft}
            style={{ paddingLeft: 24 * level }}
          >
            {c.subCategory?.length > 0 ? (
              <>
                {opened.includes(c.reference) && (
                  <ExpandLess
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleOpened(c.reference)}
                  />
                )}
                {!opened.includes(c.reference) && (
                  <ExpandMore
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleOpened(c.reference)}
                  />
                )}
              </>
            ) : (
              <div className={classes.paddingLeft} />
            )}
            <Text
              type={level === 0 ? "important" : "text"}
              className={level === 1 ? classes.subCat : ""}
              style={c.subCategory?.length > 0 ? { cursor: "pointer" } : {}}
              onClick={() => toggleOpened(c.reference)}
            >
              {c.title}
            </Text>
            <Text type="secondary" className={classes.textId}>
              {` (ID : ${c.reference})`}
              {c.products.length > 0 &&
                ` - ${c.products.length} produit${
                  c.products.length > 1 ? "s" : ""
                }`}
            </Text>
          </div>

          <div>
            {!isReferencement && (
              <>
                <Button
                  color="secondary"
                  variant="text"
                  style={{
                    minWidth: 30,
                    marginRight: 10,
                    border: "none",
                    boxShadow: "none",
                    backgroundColor: "transparent",
                    padding: 3,
                    color: "#457dd9",
                  }}
                  disableElevation
                  disableRipple
                  onClick={() => {
                    toggleOpenPopin(true);
                    setActiveCategory(c);
                  }}
                >
                  <Edit2LineIcon />
                </Button>

                <Button
                  color="secondary"
                  variant="text"
                  style={{
                    minWidth: 30,
                    marginRight: 10,
                    border: "none",
                    boxShadow: "none",
                    backgroundColor: "transparent",
                    padding: 3,
                    color: "#457dd9",
                  }}
                  disableElevation
                  disableRipple
                  onClick={() => handleDelete(c.id)}
                  confirm
                  confirmMessage={`Supprimer la catégorie ${c.title} ?`}
                >
                  <DeleteIcon />
                </Button>
              </>
            )}
          </div>
        </ListItem>
        {c.subCategory?.length > 0 && (
          <Collapse
            in={opened.includes(c.reference)}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {c.subCategory.map((subC, j) =>
                renderCategory(level + 1, subC, j)
              )}
            </List>
          </Collapse>
        )}
      </Fragment>
    );
  };

  return (
    <Layout title="Gérer les catégories">
      <InfoContainer
        leftHeader={
          <Box display="flex" alignItems="center">
            <ListCheck2Icon />
            <Text type="h3" className={classes.title}>
              Catégories
            </Text>
          </Box>
        }
        rightHeader={
          <Button
            variant="outlined"
            onClick={() => {
              toggleOpenPopin(true);
              setActiveCategory(undefined);
            }}
            startIcon={<AddIcon />}
          >
            Ajouter une catégorie
          </Button>
        }
      >
        <List className={classes.muiList} style={{ width: "100%" }}>
          {categories.groupedCategories
            ?.slice()
            .sort((a: CategoryType, b: CategoryType) => {
              const aIsReferencing = isReferencementCategory(a);
              const bIsReferencing = isReferencementCategory(b);

              // If both are referencing or both are not, maintain original order
              if (aIsReferencing === bIsReferencing) return 0;

              // If a is referencing, it should come after b
              if (aIsReferencing) return 1;

              // If b is referencing, it should come after a
              return -1;
            })
            .map((c: CategoryType, i: number) => renderCategory(0, c, i))}
        </List>

        <Popin openPopin={openPopin} toggleDrawer={toggleOpenPopin}>
          <CategoryForm
            categories={categories.orderedCategories?.filter(
              (oC: any) => oC.type !== "sous-sous-cat"
            )}
            onSubmit={onSubmitHandler}
            category={activeCategory}
            setActiveCategory={setActiveCategory}
            toggleOpenPopin={toggleOpenPopin}
          />
        </Popin>
      </InfoContainer>
    </Layout>
  );
}

export default Category;
